import React, { useState, useEffect } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { db, auth } from "../firebase";
import { doc, getDoc } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { usePageTitle } from "../hooks/usePageTitle";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  searchCompanyData,
  recoverCompanyData,
  listAllCompaniesWithData,
  recoverExportalimData,
} from "../utils/recoverCompanyData";
import "../styles/societes.css";

const DataRecovery: React.FC = () => {
  usePageTitle("Récupération de Données");
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [oldCompanyName, setOldCompanyName] = useState("");
  const [newCompanyName, setNewCompanyName] = useState("");
  const [collection, setCollection] = useState<"companies" | "foodinnovCompanies">("foodinnovCompanies");
  const [searchResult, setSearchResult] = useState<any>(null);
  const [allCompanies, setAllCompanies] = useState<Array<{ key: string; name: string; opportunities: number; invoices: number; devis: number }>>([]);

  useEffect(() => {
    loadAllCompanies();
  }, [collection]);

  const loadAllCompanies = async () => {
    setLoading(true);
    try {
      const companies = await listAllCompaniesWithData(collection);
      setAllCompanies(companies);
    } catch (error) {
      console.error("Erreur lors du chargement:", error);
      toast.error("Erreur lors du chargement de la liste");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!oldCompanyName.trim()) {
      toast.error("Veuillez entrer un nom de société");
      return;
    }

    setLoading(true);
    try {
      const result = await searchCompanyData(oldCompanyName, collection);
      setSearchResult(result);
      
      if (result.found) {
        toast.success(result.message);
      } else {
        toast.warning(result.message);
      }
    } catch (error: any) {
      toast.error(`Erreur: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleRecover = async () => {
    if (!oldCompanyName.trim() || !newCompanyName.trim()) {
      toast.error("Veuillez entrer l'ancien et le nouveau nom de société");
      return;
    }

    if (!window.confirm(
      `Êtes-vous sûr de vouloir récupérer les données de "${oldCompanyName}" vers "${newCompanyName}" ?\n\n` +
      `Cette opération va fusionner les données récupérées avec les données existantes de "${newCompanyName}" si elles existent.`
    )) {
      return;
    }

    setLoading(true);
    try {
      const result = await recoverCompanyData(oldCompanyName, newCompanyName, collection);
      
      if (result.success) {
        toast.success(result.message, { autoClose: 5000 });
        setSearchResult(null);
        setOldCompanyName("");
        setNewCompanyName("");
        await loadAllCompanies();
      } else {
        toast.error(result.message);
      }
    } catch (error: any) {
      toast.error(`Erreur: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleRecoverExportalim = async () => {
    if (!window.confirm(
      `Êtes-vous sûr de vouloir récupérer les données de "CLIENT EXPORTALIM ENR" ?\n\n` +
      `Cette opération va rechercher et récupérer toutes les données perdues pour cette société.`
    )) {
      return;
    }

    setLoading(true);
    try {
      const result = await recoverExportalimData(collection);
      
      if (result.success) {
        toast.success(result.message, { autoClose: 5000 });
        setSearchResult(null);
        setOldCompanyName("");
        setNewCompanyName("");
        await loadAllCompanies();
      } else {
        toast.error(result.message);
      }
    } catch (error: any) {
      toast.error(`Erreur: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await auth.signOut();
      navigate('/login');
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
    }
  };

  return (
    <div className="societes-page flex flex-col min-h-screen">
      <Header
        isAuthenticated={true}
        onLoginClick={() => navigate('/login')}
        onHomeClick={() => navigate('/')}
        onLogoutClick={handleLogout}
      />
      <main className="societes-container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '2rem' }}>
        <h1 className="societes-title">Récupération de Données Perdues</h1>

        <div style={{ marginBottom: '2rem', padding: '1.5rem', backgroundColor: '#fff3cd', borderRadius: '8px', border: '1px solid #ffc107' }}>
          <h3 style={{ marginTop: 0, color: '#856404' }}>⚠️ Important</h3>
          <p style={{ color: '#856404', marginBottom: 0 }}>
            Cet outil permet de rechercher et récupérer les données (demandes, factures, devis) 
            qui ont pu être perdues lors de la modification du nom d'une société.
            <br />
            <strong>Note :</strong> Si les données ont été complètement supprimées de Firestore, 
            elles ne peuvent être récupérées que via une sauvegarde Firestore.
          </p>
        </div>

        <div style={{ marginBottom: '2rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
            Collection à analyser :
          </label>
          <select
            value={collection}
            onChange={(e) => setCollection(e.target.value as "companies" | "foodinnovCompanies")}
            style={{
              padding: '0.5rem',
              fontSize: '1rem',
              border: '1px solid #ccc',
              borderRadius: '4px',
              width: '100%',
              maxWidth: '300px'
            }}
          >
            <option value="foodinnovCompanies">Sociétés FoodInnov</option>
            <option value="companies">Sociétés RegExport</option>
          </select>
        </div>

        <div style={{ marginBottom: '2rem', padding: '1.5rem', backgroundColor: '#e7f3ff', borderRadius: '8px', border: '2px solid #2196F3' }}>
          <h2 style={{ marginTop: 0, color: '#1976D2' }}>⚡ Récupération Rapide : CLIENT EXPORTALIM ENR</h2>
          <p style={{ marginBottom: '1rem', color: '#424242' }}>
            Bouton de récupération directe pour la société "CLIENT EXPORTALIM ENR"
          </p>
          <button
            onClick={handleRecoverExportalim}
            disabled={loading}
            className="add-societe-button"
            style={{
              backgroundColor: '#2196F3',
              color: 'white',
              fontWeight: 'bold',
              fontSize: '1.1rem',
              padding: '0.75rem 1.5rem'
            }}
          >
            {loading ? "Récupération en cours..." : "🔍 Récupérer CLIENT EXPORTALIM ENR"}
          </button>
        </div>

        <div style={{ marginBottom: '2rem', padding: '1.5rem', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
          <h2 style={{ marginTop: 0 }}>Recherche de Données</h2>
          
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
              Ancien nom de la société (nom perdu) :
            </label>
            <input
              type="text"
              value={oldCompanyName}
              onChange={(e) => setOldCompanyName(e.target.value)}
              placeholder="Ex: FOODINNOV"
              style={{
                padding: '0.5rem',
                fontSize: '1rem',
                border: '1px solid #ccc',
                borderRadius: '4px',
                width: '100%',
                maxWidth: '400px'
              }}
            />
          </div>

          <button
            onClick={handleSearch}
            disabled={loading}
            className="add-societe-button"
            style={{ marginBottom: '1rem' }}
          >
            {loading ? "Recherche en cours..." : "Rechercher les données"}
          </button>

          {searchResult && (
            <div style={{
              marginTop: '1rem',
              padding: '1rem',
              backgroundColor: searchResult.found ? '#d4edda' : '#f8d7da',
              borderRadius: '4px',
              border: `1px solid ${searchResult.found ? '#c3e6cb' : '#f5c6cb'}`
            }}>
              <h3 style={{ marginTop: 0, color: searchResult.found ? '#155724' : '#721c24' }}>
                {searchResult.found ? "✓ Données trouvées !" : "✗ Aucune donnée trouvée"}
              </h3>
              <p style={{ color: searchResult.found ? '#155724' : '#721c24' }}>
                {searchResult.message}
              </p>
              {searchResult.found && searchResult.data && (
                <div style={{ marginTop: '1rem' }}>
                  <p><strong>Données trouvées :</strong></p>
                  <ul>
                    <li>Demandes (opportunities) : {searchResult.data.opportunities?.length || 0}</li>
                    <li>Factures (invoices) : {searchResult.data.invoices?.length || 0}</li>
                    <li>Devis (devis) : {searchResult.data.devis?.length || 0}</li>
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        {searchResult?.found && (
          <div style={{ marginBottom: '2rem', padding: '1.5rem', backgroundColor: '#d1ecf1', borderRadius: '8px' }}>
            <h2 style={{ marginTop: 0 }}>Récupération des Données</h2>
            
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                Nouveau nom de la société (nom actuel) :
              </label>
              <input
                type="text"
                value={newCompanyName}
                onChange={(e) => setNewCompanyName(e.target.value)}
                placeholder="Ex: FoodInnov"
                style={{
                  padding: '0.5rem',
                  fontSize: '1rem',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  width: '100%',
                  maxWidth: '400px'
                }}
              />
            </div>

            <button
              onClick={handleRecover}
              disabled={loading || !newCompanyName.trim()}
              className="add-societe-button"
              style={{
                backgroundColor: '#28a745',
                color: 'white'
              }}
            >
              {loading ? "Récupération en cours..." : "Récupérer les données"}
            </button>
          </div>
        )}

        <div style={{ marginTop: '2rem' }}>
          <h2>Liste de toutes les sociétés ({collection})</h2>
          <button
            onClick={loadAllCompanies}
            disabled={loading}
            className="add-societe-button"
            style={{ marginBottom: '1rem' }}
          >
            {loading ? "Chargement..." : "Actualiser la liste"}
          </button>

          {allCompanies.length > 0 ? (
            <table className="societes-table">
              <thead>
                <tr>
                  <th>Clé Firestore</th>
                  <th>Nom de la société</th>
                  <th>Demandes</th>
                  <th>Factures</th>
                  <th>Devis</th>
                </tr>
              </thead>
              <tbody>
                {allCompanies.map((company) => (
                  <tr key={company.key}>
                    <td style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>{company.key}</td>
                    <td>{company.name}</td>
                    <td>{company.opportunities}</td>
                    <td>{company.invoices}</td>
                    <td>{company.devis}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p>Aucune société trouvée.</p>
          )}
        </div>
      </main>

      <ToastContainer position="top-right" autoClose={3000} />
      <Footer />
    </div>
  );
};

export default DataRecovery;

